// src/mobile_pages/const/DailyView.jsx
import React from "react";
import { useParams } from "react-router-dom";
import { Cloud, Thermometer, Droplets, Wind, ClipboardList, Package, Truck, Users } from "lucide-react";
import MobileFormViewManager from "../../components/common/mobile/MobileFormViewManager";

/* ---------------------------------------------------------
   💡 공사일보 본문을 렌더링하는 컴포넌트 (MobileDailyBody)
   - 모바일 최적화 카드 형식
---------------------------------------------------------- */
function MobileDailyBody({ meta, data }) {
  return (
    <div className="space-y-4">
      {/* ===================================== 📌 날씨 정보 ===================================== */}
      <section className="rounded-2xl bg-white border border-slate-200 overflow-hidden">
        <div className="px-4 py-3 bg-slate-50 border-b border-slate-200">
          <div className="flex items-center gap-2">
            <Cloud size={16} className="text-blue-500" />
            <h3 className="text-sm font-semibold text-slate-700">날씨 정보</h3>
          </div>
        </div>
        <div className="p-4">
          {/* 날씨 + 기온 */}
          <div className="grid grid-cols-2 gap-3 mb-3">
            <InfoItem icon={<Cloud size={14} />} label="날씨" value={meta.weather || "-"} />
            <InfoItem icon={<Thermometer size={14} />} label="기온" value={`${meta.temp_min ?? "-"}℃ / ${meta.temp_max ?? "-"}℃`} />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <InfoItem label="평균기온" value={`${meta.temp_avg ?? "-"}℃`} small />
            <InfoItem label="강수량" value={`${meta.precip_total ?? 0}mm`} small />
            <InfoItem label="풍속" value={`${meta.wind_speed_avg ?? "-"}m/s`} small />
          </div>
        </div>
      </section>

      {/* ===================================== 📌 작업사항 ===================================== */}
      <section className="rounded-2xl bg-white border border-slate-200 overflow-hidden">
        <div className="px-4 py-3 bg-slate-50 border-b border-slate-200">
          <div className="flex items-center gap-2">
            <ClipboardList size={16} className="text-green-500" />
            <h3 className="text-sm font-semibold text-slate-700">작업사항</h3>
          </div>
        </div>
        <div className="p-4 space-y-4">
          {/* 금일 작업 */}
          <div>
            <p className="text-xs font-medium text-slate-500 mb-1">금일 작업사항</p>
            <div className="bg-slate-50 rounded-xl p-3 text-sm text-slate-700 whitespace-pre-wrap min-h-[60px]">
              {meta.today_work || "작성된 내용 없음"}
            </div>
          </div>
          {/* 명일 작업 */}
          <div>
            <p className="text-xs font-medium text-slate-500 mb-1">명일 작업(예정) 사항</p>
            <div className="bg-slate-50 rounded-xl p-3 text-sm text-slate-700 whitespace-pre-wrap min-h-[60px]">
              {meta.tomorrow_work || "작성된 내용 없음"}
            </div>
          </div>
        </div>
      </section>

      {/* ===================================== 📌 자재 현황 ===================================== */}
      <section className="rounded-2xl bg-white border border-slate-200 overflow-hidden">
        <div className="px-4 py-3 bg-slate-50 border-b border-slate-200">
          <div className="flex items-center gap-2">
            <Package size={16} className="text-orange-500" />
            <h3 className="text-sm font-semibold text-slate-700">자재 현황</h3>
            <span className="text-xs text-slate-400">({data.materials?.length || 0}건)</span>
          </div>
        </div>
        <div className="p-4">
          {data.materials?.length > 0 ? (
            <div className="space-y-2">
              {data.materials.map((r, i) => {
                const total = (r.prev_total || 0) + (r.qty || 0);
                return (
                  <div key={i} className="bg-slate-50 rounded-xl p-3">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-slate-800">{r.name}</span>
                      <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">{r.unit}</span>
                    </div>
                    <p className="text-xs text-slate-500 mb-2">{r.spec || "-"}</p>
                    <div className="grid grid-cols-3 gap-2 text-xs">
                      <div className="text-center">
                        <p className="text-slate-400">전일누계</p>
                        <p className="font-medium text-slate-700">{r.prev_total || 0}</p>
                      </div>
                      <div className="text-center">
                        <p className="text-slate-400">금일</p>
                        <p className="font-medium text-blue-600">{r.qty || 0}</p>
                      </div>
                      <div className="text-center">
                        <p className="text-slate-400">누계</p>
                        <p className="font-semibold text-slate-800">{total}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-sm text-slate-400 text-center py-4">등록된 자재가 없습니다.</p>
          )}
        </div>
      </section>

      {/* ===================================== 📌 장비 현황 ===================================== */}
      <section className="rounded-2xl bg-white border border-slate-200 overflow-hidden">
        <div className="px-4 py-3 bg-slate-50 border-b border-slate-200">
          <div className="flex items-center gap-2">
            <Truck size={16} className="text-purple-500" />
            <h3 className="text-sm font-semibold text-slate-700">장비 현황</h3>
            <span className="text-xs text-slate-400">({data.equipments?.length || 0}건)</span>
          </div>
        </div>
        <div className="p-4">
          {data.equipments?.length > 0 ? (
            <div className="space-y-2">
              {data.equipments.map((r, i) => {
                const total = (r.prev_total || 0) + (r.qty || 0);
                return (
                  <div key={i} className="bg-slate-50 rounded-xl p-3">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-slate-800">{r.name}</span>
                    </div>
                    <p className="text-xs text-slate-500 mb-2">{r.spec || "-"}</p>
                    <div className="grid grid-cols-3 gap-2 text-xs">
                      <div className="text-center">
                        <p className="text-slate-400">전일누계</p>
                        <p className="font-medium text-slate-700">{r.prev_total || 0}</p>
                      </div>
                      <div className="text-center">
                        <p className="text-slate-400">금일</p>
                        <p className="font-medium text-purple-600">{r.qty || 0}</p>
                      </div>
                      <div className="text-center">
                        <p className="text-slate-400">누계</p>
                        <p className="font-semibold text-slate-800">{total}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-sm text-slate-400 text-center py-4">등록된 장비가 없습니다.</p>
          )}
        </div>
      </section>

      {/* ===================================== 📌 인원 현황 ===================================== */}
      <section className="rounded-2xl bg-white border border-slate-200 overflow-hidden">
        <div className="px-4 py-3 bg-slate-50 border-b border-slate-200">
          <div className="flex items-center gap-2">
            <Users size={16} className="text-teal-500" />
            <h3 className="text-sm font-semibold text-slate-700">인원 현황</h3>
            <span className="text-xs text-slate-400">({data.personnels?.length || 0}건)</span>
          </div>
        </div>
        <div className="p-4">
          {data.personnels?.length > 0 ? (
            <div className="space-y-2">
              {data.personnels.map((r, i) => {
                const total = (r.prev_total || 0) + (r.qty || 0);
                return (
                  <div key={i} className="bg-slate-50 rounded-xl p-3">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-slate-800">{r.work_type}</span>
                      <span className="text-xs bg-teal-100 text-teal-700 px-2 py-0.5 rounded-full">{r.category}</span>
                    </div>
                    <div className="grid grid-cols-3 gap-2 text-xs">
                      <div className="text-center">
                        <p className="text-slate-400">전일누계</p>
                        <p className="font-medium text-slate-700">{r.prev_total || 0}</p>
                      </div>
                      <div className="text-center">
                        <p className="text-slate-400">금일</p>
                        <p className="font-medium text-teal-600">{r.qty || 0}</p>
                      </div>
                      <div className="text-center">
                        <p className="text-slate-400">누계</p>
                        <p className="font-semibold text-slate-800">{total}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-sm text-slate-400 text-center py-4">등록된 인원이 없습니다.</p>
          )}
        </div>
      </section>
    </div>
  );
}

// 정보 아이템 컴포넌트
function InfoItem({ icon, label, value, small = false }) {
  return (
    <div className={`${small ? 'text-center' : ''}`}>
      <div className={`flex items-center gap-1 ${small ? 'justify-center' : ''} mb-0.5`}>
        {icon && <span className="text-slate-400">{icon}</span>}
        <span className="text-xs text-slate-400">{label}</span>
      </div>
      <p className={`${small ? 'text-xs' : 'text-sm'} font-medium text-slate-700`}>{value}</p>
    </div>
  );
}

/* ---------------------------------------------------------
   💡 DailyView: MobileFormViewManager 호출 + MobileDailyBody 포함
---------------------------------------------------------- */
export default function DailyView() {
  const { id: project, dns } = useParams();

  return (
    <MobileFormViewManager
      params={{
        project,
        dns,
        module: "const",
        doc_type: "DAILY",
      }}
      doc_name="공사일보"
    >
      <MobileDailyBody />
    </MobileFormViewManager>
  );
}